import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useIndiaSpecificPrivacyPolicy = () => {
  const [active, setActive] = useState("info");
  const { brandName, normalizedBrand,phone } = getBrandName();

  const sections = [
    { id: "info", title: "Information We Collect" },
    { id: "consent", title: "Consent: Your Control, Your Rights" },
    { id: "rights", title: "Your Rights Under the DPDP Act, 2023" },
    { id: "purpose", title: "Purpose of Data Collection and Use" },
    { id: "security", title: "Data Protection & Security Measures" },
    { id: "grievance", title: "Grievance Redressal Mechanism" },
    { id: "retention", title: "Data Retention & Deletion" },
    { id: "children", title: "Children’s Privacy" },
    { id: "thirdparty", title: "Third-Party Processors & Data Sharing" },
    { id: "crossborder", title: "Cross-Border Data Transfer" },
    { id: "cookies", title: "Cookies & Tracking Technologies" },
    { id: "updates", title: "Policy Updates & Notifications" },
    { id: "contact", title: "Contact Us" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const contentMap = {
    info: `
a. Personal Identifiable Information (PII):  
• Full Name  
• Email Address  
• Phone Number  
• Business Name  
• Login credentials (secured and encrypted)

b. Device & Technical Information:  
• IP Address  
• Browser type, version, and language  
• OS and device model  
• Device identifiers  
• Pages visited, time spent, and interaction logs

c. Voluntary Information:  
• Contact form submissions  
• Survey responses  
• Support tickets  
• Feature requests/testimonials

d. Usage Analytics (Non-PII):  
• Clicks, scrolls, navigation  
• Feature usage/session duration  
• Bounce rate & engagement

❌ No collection of passwords, Aadhaar, financial or health data without explicit consent.
`,

    consent: `
• "Consent First" approach with clear, specific disclosures  
• Affirmative, informed opt-in required  
• Consent revocable at any time via 📧 ${email}  
• No pre-ticked boxes or bundled consent
`,

    rights: `
a. Right to Access:  
• Request your personal data  
• View usage purposes  
• Identify third-party processors

b. Right to Correction:  
• Fix outdated or inaccurate data

c. Right to Erasure (Right to Be Forgotten):  
• Request deletion when purpose ends, or consent is withdrawn

d. Right to Withdraw Consent:  
• Can be done anytime without penalty

e. Right to Nominate:  
• Nominate a legal rep for your rights in case of death/incapacity

📧 Reach out via our Grievance Officer (Section 6)
`,

    purpose: `
a. To Provide Services  
• Account creation  
• Dashboard generation  
• API access

b. To Enhance Experience  
• Save preferences  
• Improve UX based on behavior

c. To Communicate  
• Notify about updates or outages  
• Respond to tickets  
• Deliver alerts or policy changes

d. To Comply With Laws  
• Store logs for audits  
• Fulfill legal requests

❌ No advertising or sale of your data  
❌ No unsolicited marketing without opt-in
`,

    security: `
a. Encryption  
• AES-256 for data at rest  
• TLS 1.2+ (HTTPS) in transit

b. Access Controls  
• Role-Based Access Control (RBAC)  
• IP whitelisting, session timeout

c. Infrastructure  
• ISO/IEC 27001 certified (e.g., AWS/GCP)  
• Redundant, secure backup locations

d. Monitoring  
• Vulnerability scans  
• Logging & anomaly alerts
`,

    grievance: `
📧 Email: ${email}  
📞 Contact: ${phone}  
🌐 Website: ${website}  

• Acknowledgement in 48 hours  
• Resolution within 7–15 working days (as per Indian law)
`,

    retention: `
• Retained only as long as necessary or legally required  
• Deleted or anonymized afterward  
• Early deletion requests accepted via Grievance Officer (subject to legal restrictions)
`,

    children: `
• No intentional collection of data from minors (<18)  
• Detected accounts will be deleted  
• Guardians notified if contact is available  
• Controls strengthened against recurrence
`,

    thirdparty: `
• Used for:  
  - Hosting (AWS/GCP)  
  - Communication (email/chat)  
  - Analytics (anonymized)

• Bound by DPAs  
• Must meet ${brandName}'s security standards  
❌ No ad-sharing without opt-in
`,

    crossborder: `
• Data may be stored outside India  
• Encryption and global compliance ensured  
• Rights enforceable regardless of location  
✅ See Cross-Border Data Policy for full details
`,

    cookies: `
• Session cookies for login  
• Analytics cookies (e.g., Google Analytics)  
• Browser controls available  
• Reject non-essential cookies  
❌ No ad tracking or third-party marketing
`,

    updates: `
• Triggered by legal changes, feature updates, or infra upgrades  
• Published with new Effective Date  
• Notified via in-app alerts/email  
• Continued use = Acceptance
`,

    contact: `
    • Email: ${normalizedBrand}@gmail.com  
    • Contact: ${phone}  
    • Website: www.${normalizedBrand}.com
Use for: Questions, access requests, or grievances
`,
  };

  const generateContent = (id) => {
    const lines = contentMap[id]?.split("\n") || [];
    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (line.includes("|")) {
        flushList();
        const [left, right] = line.split("|").map((s) => s.trim());
        contentElements.push(
          <p key={`row-${index}`}>
            <strong>{left}</strong>: {right}
          </p>
        );
      } else if (/^•|❌|✅|📧|📞|🌐/.test(line)) {
        items.push(line.replace(/^(\s*•|\s*[\u{1F300}-\u{1F6FF}])\s*/u, ""));
      } else if (/^[a-z]\./i.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
